/**
 * What travels on hg-realtime's staff stream.
 *
 * Every message is one object, `{id, type, occurred_at, data}`; its `event:`
 * is the type. The types are listed here because a browser's `EventSource`
 * hands a named event only to whoever asked for that name: a type the
 * service adds later is not heard until it is added to this list.
 */

/** The service's own events on a stream. */
export const SERVICE_EVENT_TYPES = ["connected", "resync", "closing"] as const;

/** Where a specialist at work is now; sent only to a stream opened with `positions=true`. */
export const POSITION_EVENT_TYPE = "specialist.position";

/** hg-order's events as staff are sent them (hg-realtime's catalogue). */
export const ORDER_EVENT_TYPES = [
  "visit.status_changed",
  "visit.assigned",
  "visit.released",
  "visit.cancelled",
  "visit.completed",
  "visit.offered",
  "visit.offer_closed",
  "visit.escalated",
  // Staff's queue: who has a row of it changed, or it left the queue.
  "escalation.claimed",
  "escalation.unclaimed",
  "escalation.taken_over",
  "escalation.resolved",
  "visit.lines_changed",
  "visit.rated",
  "visit.rating_moderated",
  "visit.reported",
  "visit.report_confirmed",
  "visit.disputed",
  "visit.dispute_closed",
  "specialist.created",
  "specialist.status_changed",
  "specialist.presence_changed",
] as const;

/**
 * What is of no area, carried to the stream's desks: a message in a support
 * thread (hg-content's `support.message_created`, the support desk); to the
 * money desk, a wallet that moved (hg-billing's `wallet.changed`: whose
 * wallets, `provider_ids`, never a sum) and an adjustment that began to wait
 * for a second person or was decided (`adjustment.requested`,
 * `adjustment.decided`: its ids and `state`, never its sum).
 */
export const DESK_EVENT_TYPES = [
  "support.message_created",
  "wallet.changed",
  "adjustment.requested",
  "adjustment.decided",
] as const;

export const STREAM_EVENT_TYPES: readonly string[] = [
  ...SERVICE_EVENT_TYPES,
  POSITION_EVENT_TYPE,
  ...ORDER_EVENT_TYPES,
  ...DESK_EVENT_TYPES,
];

/** A message of the stream, as the screens are handed it. */
export interface LiveEvent {
  /** The event's type: `visit.assigned`, `resync`... */
  type: string;
  /** The event's own id; null for a message that has none. */
  id: string | null;
  /** When it happened, as the service says; null when it does not say. */
  occurredAt: string | null;
  /** The event as staff see it. A field the type does not have is simply absent. */
  data: Readonly<Record<string, unknown>>;
  /**
   * Of `connected` alone, the stream's own word: true when the stream had
   * been connected before since it was started, so this is a return (after
   * a break, or opened anew for the points). What is of no area and moved in
   * between (a wallet, an adjustment, a support message) was not sent again.
   * The first `connected` of a page has nothing in between to make up for.
   */
  reconnected?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads the `data:` of a message. Anything that is not the object the
 * protocol promises is null: it is let go, never guessed at.
 */
export function parseLiveEvent(type: string, raw: unknown): LiveEvent | null {
  if (typeof raw !== "string") return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const data = parsed["data"];
  return {
    type,
    id: typeof parsed["id"] === "string" ? parsed["id"] : null,
    occurredAt: typeof parsed["occurred_at"] === "string" ? parsed["occurred_at"] : null,
    data: isRecord(data) ? data : {},
  };
}

/** A text field of an event, or null. */
export function textOf(event: LiveEvent, field: string): string | null {
  const value = event.data[field];
  return typeof value === "string" && value !== "" ? value : null;
}

/** A number field of an event, or null. */
export function numberOf(event: LiveEvent, field: string): number | null {
  const value = event.data[field];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * An event's place in hg-order's stream, from the `id:` of its message:
 * `<when the stream was made>-<how far into it>`. Null for an id that is not
 * of that shape.
 */
export function placeOf(streamId: string): { stream: string; sequence: number } | null {
  const cut = streamId.lastIndexOf("-");
  if (cut <= 0) return null;
  const sequence = Number(streamId.slice(cut + 1));
  if (!Number.isSafeInteger(sequence) || sequence < 0) return null;
  return { stream: streamId.slice(0, cut), sequence };
}
